import {
  ICareProposal,
  IDeliveryAddress,
  IDeliveryInterval,
  IHistoryArticleLine,
  IOrder,
  IProposedArticleLine,
  OrderStatus,
  PrescriptionType,
  ServiceCostEstimateApprovalInfo,
  ServiceCostEstimateRejectionInfo,
} from '@alberta/konexi-shared';
import { groupBy } from 'lodash';
import moment from 'moment';
import { addBusinessDays } from 'src/app/common/date/business-days';
import { CareProposalDB } from 'src/app/common/repository/databases';
import { createObject } from 'src/app/common/tracking/object-creator';
import { ignore, removeTime, tracked } from 'src/app/common/tracking/tracker';
import { BaseViewModel } from 'src/app/common/viewmodel/base-view-model';
import { QueryService } from 'src/app/shared/services/query/query.service';

import { IOrderMaximumQuantity } from '../contracts/order-maximum-quantity';
import {
  getContractAndStandardCareProposal,
  loadArticlesOfArticleLines,
  transformProposedArticleForSeriesOrder,
} from '../order-functions';
import {
  getMaxQuantityForOrderedArticleLine,
  getOrderMaxQuantityForProposedArticleLine,
  updateMaxQuantityInOrderedArticleLines,
} from '../order-max-quantity-functions';
import { normalizeDosageQuantityTo30Days } from '../series-order-functions';
import { TenantConfigService } from './../../../shared/services/tenant-config/tenant-config.service';
import { OrderedArticleLineViewModel } from './ordered-article-line-view-model';

export class SeriesOrderViewModel extends BaseViewModel implements IOrder {
  dayOfDelivery: number;
  @removeTime()
  nextDelivery: Date;
  rootId: string;
  _id: string;
  timestamp: Date;
  archived: boolean;
  regionId: string;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
  salesId: string;
  patientId: string;
  therapyId: number;
  therapyTypeId: number;
  location: number;
  updated: Date;
  @removeTime()
  deliveryDate: Date;
  @removeTime()
  startDate: Date = new Date();
  @removeTime()
  endDate: Date = new Date();
  deliveryInterval: IDeliveryInterval;
  deliveryAddress: IDeliveryAddress;
  deliveryInformation: string;
  startTime: string;
  deliveryNote: string;

  doctorId: string;
  hospitalId: string;
  @ignore(true)
  isHospitalPrescriber: boolean;

  pharmacyId: string;

  status: OrderStatus;

  isInitialCare: boolean;
  @tracked({ factory: createObject(OrderedArticleLineViewModel) })
  orderedArticleLines: OrderedArticleLineViewModel[] = [];
  articleLineHistory: IHistoryArticleLine[];

  isSeriesOrder: boolean;
  seriesOrderChanged: boolean;
  deliveryAddressChanged: boolean;

  annotation: string;

  createdBy: string;
  creationDate: Date;

  accountingMonth: number[];
  careProposalId: string;
  deliveryDateChanged: boolean;
  alreadyTransmitted: boolean;
  serviceCostEstimateApprovalOrRejection: ServiceCostEstimateRejectionInfo | ServiceCostEstimateApprovalInfo;
  hasFollowUpOrder: boolean;
  transmittedAt: string;
  /**
   * Indiviudal OrderViewModel Data
   */
  @ignore()
  public scope: number;
  @ignore()
  public associatedCareproposal: ICareProposal;

  constructor() {
    super();
    this.isSeriesOrder = true;
    this.seriesOrderChanged = false;
    this.deliveryDateChanged = false;
    this.isHospitalPrescriber = false;
    this.articleLineHistory = [];
  }

  /**
   *
   *
   * Initialize the Series Order with the article lines form the careproposal
   */
  public async initializeWithCareProposalData(
    careProposal: ICareProposal,
    queryService: QueryService,
    tenantConfigService: TenantConfigService
  ) {
    this.initializeVariablesFromCareProposal(careProposal, tenantConfigService);

    const articles = await loadArticlesOfArticleLines(careProposal.proposedArticleLines, queryService);

    const groupedProposedArticleLines = groupBy(careProposal.proposedArticleLines, (item: IProposedArticleLine) => {
      const foundArtilce = articles.find(article => article._id === item.articleId);
      return `${item.contractId} ${foundArtilce.productGroupId}`;
    });

    for (const key of Object.keys(groupedProposedArticleLines)) {
      const groupedProposedArticleLinesEntries = groupedProposedArticleLines[key];
      const firstProposedArticleLine = groupedProposedArticleLinesEntries[0];
      const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
        (careProposal || { standardCareProposalId: null }).standardCareProposalId,
        (firstProposedArticleLine || { contractId: null }).contractId,
        queryService
      );

      let maxQuantity: IOrderMaximumQuantity = null;
      maxQuantity = getOrderMaxQuantityForProposedArticleLine(
        contract,
        firstProposedArticleLine,
        standardCareProposal,
        null
      );

      for (const proposedArticleLine of groupedProposedArticleLinesEntries) {
        const article = articles.find(article1 => article1._id === proposedArticleLine.articleId);
        if (!article) {
          throw new Error('Article from proposedArticleline in careproposal not found!');
        }
        const packagingUnit = article.findPackagingUnit(proposedArticleLine.dosage.packagingId);
        if (!packagingUnit) {
          throw new Error('PackagingUnit from careproposal not found!');
        }
        const articleToOrder = new OrderedArticleLineViewModel();
        articleToOrder.articleId = proposedArticleLine.articleId;
        articleToOrder.associatedArticle = article;
        articleToOrder.articleCustomerNumber = article.customerNumber;
        articleToOrder.dosage.packagingId = packagingUnit._id;
        articleToOrder.dosage.quantity = proposedArticleLine.dosage.quantity;
        articleToOrder.unit = packagingUnit.unit;
        articleToOrder.associatedProposedArticleLine = proposedArticleLine;
        if (article.prescriptionType && article.prescriptionType === PrescriptionType.AidsForUse) {
          articleToOrder.active = false;
          articleToOrder.notAutomaticDelivery = true;
        }

        articleToOrder.associatedQuantityPerUnit = packagingUnit;
        if (maxQuantity) {
          articleToOrder.maxAmount = maxQuantity;
          articleToOrder.maxAmount.currentQuantity += normalizeDosageQuantityTo30Days(
            articleToOrder.dosage,
            articleToOrder.associatedQuantityPerUnit.quantity
          );
        }
        articleToOrder.dosage.duration = { ...proposedArticleLine.dosage.duration };

        if (contract) {
          articleToOrder.associatedContract = contract;
          articleToOrder.contractID = contract._id;
        }
        this.orderedArticleLines.push(articleToOrder);
      }
    }
  }

  private async initializeVariablesFromCareProposal(
    careProposal: ICareProposal,
    tenantConfigService: TenantConfigService
  ) {
    this.associatedCareproposal = careProposal;
    this.careProposalId = careProposal._id;
    this.therapyId = careProposal.therapyId;
    this.therapyTypeId = careProposal.therapyTypeId;
    if (careProposal.location) {
      this.location = careProposal.location;
    }
    this.status = OrderStatus.Created;
    this.creationDate = new Date();
    this.regionId = careProposal.regionId;
    if (await tenantConfigService.mustSetDeliveryDate()) {
      this.deliveryDate = this.startDate = addBusinessDays(new Date(), 2);
    }

    this.patientId = careProposal.patientId;
    this.doctorId = careProposal.doctorId;
    if (careProposal.pharmacyId) {
      this.pharmacyId = careProposal.pharmacyId;
    }
  }

  public cleanViewModel() {
    // remove unnessary Articlelines
    for (let index = this.orderedArticleLines.length - 1; index >= 0; index--) {
      if (!this.orderedArticleLines[index].active) {
        this.orderedArticleLines.splice(index, 1);
      }
    }
  }

  public async loadOrderVM(queryService: QueryService, loadNewArticleLines = true): Promise<void> {
    const careproposal = (await queryService.get(this.careProposalId, CareProposalDB)) as ICareProposal;
    if (!careproposal) {
      throw new Error('No related careproposal found!');
    }
    this.associatedCareproposal = careproposal;

    await this.populateExistingArticleLines(queryService, careproposal);

    if (loadNewArticleLines) {
      await this.addNewArticleLinesFromCareProposalToOrder(careproposal, queryService);
    }

    const groupByContractIdAndTherapyProductGroupId = (item: OrderedArticleLineViewModel) =>
      `${item.contractID} ${item.associatedArticle ? item.associatedArticle.productGroupId : undefined}`;
    const groupedArticlesLines = groupBy(this.orderedArticleLines, groupByContractIdAndTherapyProductGroupId);

    for (const key of Object.keys(groupedArticlesLines)) {
      const orderedArticleLines = groupedArticlesLines[key];
      const firstOrderedArticleLine = orderedArticleLines[0];
      const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
        (careproposal || { standardCareProposalId: null }).standardCareProposalId,
        (firstOrderedArticleLine || { contractID: null }).contractID,
        queryService
      );

      const maxQuantity = getMaxQuantityForOrderedArticleLine(
        contract,
        firstOrderedArticleLine,
        standardCareProposal,
        careproposal,
        null
      );

      updateMaxQuantityInOrderedArticleLines.call(this, orderedArticleLines, contract, maxQuantity, {
        setContract: (index, insuranceContract) =>
          (this.orderedArticleLines[index].associatedContract = insuranceContract),
        setCurrentQuantity: index => {
          this.orderedArticleLines[index].maxAmount.currentQuantity += normalizeDosageQuantityTo30Days(
            this.orderedArticleLines[index].dosage,
            this.orderedArticleLines[index].associatedQuantityPerUnit.quantity
          );
        },
      });
    }
  }

  private async addNewArticleLinesFromCareProposalToOrder(careproposal: ICareProposal, queryService: QueryService) {
    const articles = await loadArticlesOfArticleLines(careproposal.proposedArticleLines, queryService);

    // load missing Articles form Careproposal
    for (const proposedArticleLine of careproposal.proposedArticleLines) {
      if (!this.orderedArticleLines.some(orderedLine => orderedLine.articleId === proposedArticleLine.articleId)) {
        const newProposedArticleLine = transformProposedArticleForSeriesOrder(
          proposedArticleLine,
          articles.find(article => article._id === proposedArticleLine.articleId)
        );
        newProposedArticleLine.isNew = true;
        newProposedArticleLine.active = false;
        newProposedArticleLine.articleNotFoundInCareProposal = false;
        newProposedArticleLine.associatedProposedArticleLine = proposedArticleLine;
        this.orderedArticleLines.push({ ...newProposedArticleLine });
      }
    }
  }

  private async populateExistingArticleLines(queryService: QueryService, careproposal: ICareProposal) {
    const articles = await loadArticlesOfArticleLines(this.orderedArticleLines, queryService);
    // populate exsiting Articlelines
    for (const orderedArticleLine of this.orderedArticleLines) {
      const article = articles.find(article1 => article1._id === orderedArticleLine.articleId);
      orderedArticleLine.isNew = false;
      orderedArticleLine.active = orderedArticleLine.active === false ? false : true;
      if (article) {
        orderedArticleLine.associatedQuantityPerUnit = article.findPackagingUnit(orderedArticleLine.dosage.packagingId);
        orderedArticleLine.associatedArticle = article;
      }
      orderedArticleLine.associatedProposedArticleLine = careproposal.proposedArticleLines.find(item => {
        return item.articleId === orderedArticleLine.articleId;
      });
      !orderedArticleLine.associatedProposedArticleLine
        ? (orderedArticleLine.articleNotFoundInCareProposal = true)
        : (orderedArticleLine.articleNotFoundInCareProposal = false);
    }
  }
  public getDeliveryStartTime() {
    if (this.startTime) {
      return moment(`01.01.1970 ${this.startTime}`, 'DD.MM.YYYY HH:mm').utc().toDate();
    }
  }
  public setDeliveryStartTime(startTime: Date) {
    if (startTime) {
      this.startTime = moment(startTime).format('HH:mm');
    }
  }
}
